import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe } from '../contexts/StripeContext';
import { Elements, PaymentElement, useStripe as useStripeElement, useElements } from '@stripe/react-stripe-js';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Payment form component for handling Stripe payment
function PaymentForm({ clientSecret, bookingData, workshop, onPaymentSuccess, onPaymentError }) {
  const stripe = useStripeElement();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required'
      });

      if (error) {
        console.error('Payment failed:', error);
        setError(error.message);
        onPaymentError(error.message);
      } else if (paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded:', paymentIntent.id);
        onPaymentSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
      onPaymentError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <PaymentElement />
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing || !clientSecret}
        className={`w-full py-3 px-4 font-medium ${
          !stripe || processing || !clientSecret
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600'
        }`}
      >
        {processing ? 'Processing Payment...' : `Pay ${formatPrice(bookingData.totalAmount)}`}
      </button>
    </form>
  );
}

function formatPrice(price) {
  if (!price) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(price);
}

function BookingForm({ workshop, onComplete, onCancel }) {
  const navigate = useNavigate();
  const { stripeInstance } = useStripe();
  const [currentStep, setCurrentStep] = useState(1);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    accommodationType: 'single', // Default to single since we simplified pricing
    emergencyContact: '',
    emergencyPhone: '',
    dietaryRestrictions: '',
    agreedToTerms: false
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
        year: 'numeric',
      month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'TBD';
    }
  };

  const getTotalPrice = () => {
    // Handle different pricing structures
    const basePrice = workshop.price || 0;
    
    // Legacy pricing structure (from mock data)
    if (workshop.pricing) {
      return formData.accommodationType === 'single' 
        ? (workshop.pricing.singleOccupancy || 0)
        : (workshop.pricing.doubleOccupancy || 0);
    }
    
    // New simplified pricing structure
    return basePrice;
  };

  const getSelectedPackagesPrice = () => {
    if (!workshop.packages || !formData.selectedPackages) return 0;
    
    return formData.selectedPackages.reduce((total, packageId) => {
      const pkg = workshop.packages.find(p => p.id === packageId);
      return total + (pkg?.price || 0);
    }, 0);
  };

  const getFinalPrice = () => {
    return getTotalPrice() + getSelectedPackagesPrice();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePackageToggle = (packageId) => {
    setFormData(prev => ({
      ...prev,
      selectedPackages: prev.selectedPackages?.includes(packageId)
        ? prev.selectedPackages.filter(id => id !== packageId)
        : [...(prev.selectedPackages || []), packageId]
    }));
  };

  const handleNext = () => {
    if (isStepValid(currentStep)) {
      if (currentStep === 3) {
        // Create payment intent when moving to payment step
        createPaymentIntent();
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const createPaymentIntent = async () => {
    try {
      setPaymentLoading(true);
      setPaymentError('');

      // Get instructor's stripe account ID
      const instructorDoc = await getDoc(doc(db, 'instructors', workshop.instructorId));
      if (!instructorDoc.exists()) {
        throw new Error('Instructor not found');
      }

      const instructorData = instructorDoc.data();
      const instructorStripeAccountId = instructorData.stripeAccountId;

      if (!instructorStripeAccountId) {
        throw new Error('Instructor has not set up payments yet. Please contact the instructor.');
      }

      // Create booking record first
      const bookingData = {
        workshopId: workshop.id,
        instructorId: workshop.instructorId,
        participantName: `${formData.firstName} ${formData.lastName}`,
        participantEmail: formData.email,
        participantPhone: formData.phone,
        accommodationType: formData.accommodationType,
        emergencyContact: formData.emergencyContact,
        emergencyPhone: formData.emergencyPhone,
        dietaryRestrictions: formData.dietaryRestrictions,
        totalAmount: getFinalPrice(),
        status: 'pending',
        createdAt: new Date(),
        selectedPackages: formData.selectedPackages || []
      };

      const bookingRef = await addDoc(collection(db, 'bookings'), bookingData);

      // Create payment intent
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: getFinalPrice() * 100, // Convert to cents
          currency: 'usd',
          bookingId: bookingRef.id,
          instructorStripeAccountId: instructorStripeAccountId,
          workshopId: workshop.id
        }),
      });

      const { clientSecret: secret, error } = await response.json();

      if (error) {
        throw new Error(error);
      }

      setClientSecret(secret);
      setCurrentStep(4);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      setPaymentError(error.message);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // Update booking status
      // Note: In a real app, you'd want to verify the payment on the server side
      navigate('/booking-success');
      onComplete();
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  const handlePaymentError = (error) => {
    setPaymentError(error);
    setCurrentStep(3); // Go back to payment step
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return formData.firstName && formData.lastName && formData.email;
      case 2:
        return formData.emergencyContact && formData.emergencyPhone;
      case 3:
        return formData.agreedToTerms;
      default:
        return true;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
  return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">First Name</label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                        required
                      />
                    </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Last Name</label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                        required
                      />
              </div>
                    </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                        required
                      />
                    </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                      />
                    </div>
                  </div>
        );

      case 2:
        return (
                    <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Emergency Contact</h3>
            
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Emergency Contact Name</label>
                      <input
                        type="text"
                        value={formData.emergencyContact}
                        onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                        required
                      />
                    </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Emergency Contact Phone</label>
                      <input
                        type="tel"
                        value={formData.emergencyPhone}
                        onChange={(e) => handleInputChange('emergencyPhone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                        required
                      />
                    </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Dietary Restrictions</label>
                      <textarea
                        value={formData.dietaryRestrictions}
                        onChange={(e) => handleInputChange('dietaryRestrictions', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                rows="3"
                placeholder="Any dietary restrictions or allergies..."
                      />
                    </div>
                  </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Review & Terms</h3>
            
            <div className="bg-gray-50 p-4 border border-gray-300 rounded">
              <h4 className="font-medium text-gray-900 mb-2">Booking Summary</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div>Workshop: {workshop.title}</div>
                <div>Date: {formatDate(workshop.startDate)} - {formatDate(workshop.endDate)}</div>
                <div>Location: {workshop.location}</div>
                <div>Total Price: {formatPrice(getFinalPrice())}</div>
                    </div>
                  </div>

                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={formData.agreedToTerms}
                        onChange={(e) => handleInputChange('agreedToTerms', e.target.checked)}
                className="mt-1"
                      />
              <label htmlFor="terms" className="text-sm text-gray-700">
                I agree to the workshop terms and conditions
                      </label>
                  </div>

                  {paymentError && (
                    <div className="p-4 bg-red-50 border border-red-200 text-center">
                      <p className="text-sm text-red-600">{paymentError}</p>
                    </div>
                  )}
                </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Payment</h3>
            {stripeInstance && clientSecret && (
                  <Elements stripe={stripeInstance} options={{ clientSecret }}>
                    <PaymentForm
                      clientSecret={clientSecret}
                      bookingData={{ totalAmount: getFinalPrice() }}
                      workshop={workshop}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                    />
                  </Elements>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6 py-8">
      <div className="w-full max-w-2xl">
        <div className="bg-white p-8 border border-gray-300 rounded-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Book Workshop</h2>
            <p className="text-gray-600">{workshop.title}</p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex space-x-4">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  {step < 4 && (
                    <div className={`w-8 h-1 mx-2 ${
                      currentStep > step ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStep()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={onCancel}
              className="px-6 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400"
            >
              Cancel
            </button>
            
            <div className="flex space-x-4">
              {currentStep > 1 && (
                  <button
                    onClick={handlePrevious}
                  className="px-6 py-2 bg-gray-600 text-white hover:bg-gray-700 border border-gray-600"
                  >
                    Previous
                  </button>
                )}

              {currentStep < 4 && (
                  <button
                    onClick={handleNext}
                    disabled={!isStepValid(currentStep) || paymentLoading}
                  className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed border border-blue-600"
                  >
                  {paymentLoading ? 'Loading...' : 'Next'}
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingForm; 